import { useForm } from "react-hook-form";
import { debounce, isEmpty } from "lodash";
import useTemplateStore from "@/utils/TemplateStore";
import { Button } from "@/components/ui/button";
import { Toaster } from "react-hot-toast";
import { toast } from "react-toastify";
import TemplateSection from "../components/TemplateSection";
import { EnhancedTextarea } from "@/components/EnhancedTextarea";
import { useEffect, useRef, useState, useCallback } from "react";
import NoteDropdown from "../components/NoteDropdown";
import ConfirmationModal from "@/components/ConfirmationModal";
import EditTitleModal from "@/components/EditTitleModal";
import { useOutletContext, useParams, useNavigate } from "react-router-dom";
import useWindowDimensions from "@/hooks/useWindowDimensions";
import useIsMobile from "@/hooks/useIsMobile";
import {
  getTemplate,
  resetTemplate,
  createNewTemplate,
  publishTemplate,
  markInactive,
  createNewAdminTemplate,
  updateVersion,
  getTemplateVersion,
  createVersion,
} from "../utils/Services";
import { Input } from "@/components/ui/input";
import NoteSectionPending from "../components/NoteSectionPending";
import {
  Stethoscope,
  Heartbeat,
  Thermometer,
  Brain,
  Users,
  Syringe,
  FirstAidKit,
  Pill,
  FunnelSimple,
  TrashSimple,
  X,
  PencilSimple,
  Check,
  FileText,
  ShareFat,
  CaretDown,
  FloppyDisk,
  CaretLeft,
  CheckFat,
} from "@phosphor-icons/react";
import { ITemplate, ITemplateVersion } from "@/utils/Types";
import { ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { NOTE, MEDICAL_VARIABLES } from "@/utils/constants";
import TemplateVersionsModal from "@/components/TemplateVersionsModal";
import TemplateVersionSelector from "@/components/TemplateVersionSelector";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ShareTemplateModal from "@/components/ShareTemplateModal";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import useUnsavedChangesStore from "@/utils/UnsavedChangesStore";
import useTemplateVersionSelectorStore from "@/utils/TemplateVersionSelector";
import UnsavedChangesDialog from "@/components/UnsavedChangesDialog";

interface OutletContextType {
  handleDeleteTemplate: Function;
  handleArchiveNote?: Function;
  handleUnarchiveNote?: Function;
  currentCreateTemplate?: ITemplate | null;
  setSaveTemplateFunction?: React.Dispatch<
    React.SetStateAction<(() => Promise<void>) | null>
  >;
}

const Template: React.FC = () => {
  const canUpdate = useRef(false);
  const firstLoad = useRef(true);
  const [canAnim, setCanAnim] = useState(false);
  const [textkey, setTextkey] = useState("");
  const [template, setTemplate] = useState({} as ITemplate);
  const [modalOpen, setModalOpen] = useState("");
  const [isFading, setIsFading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNoteMenuOpen, setIsNoteMenuOpen] = useState(false);
  const [inComingVariable, setInComingVariable] = useState(null);
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const [open, setOpen] = useState(false);
  const { addTemplate, ctgStore, templateStore } = useTemplateStore();
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [currentVersion, setCurrentVersion] = useState({} as ITemplateVersion);
  const [newVersion, setNewVersion] = useState({} as ITemplateVersion);
  const [isSavingNewVersion, setIsSavingNewVersion] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const {
    pendingNavigation,
    setPendingNavigation,
    unsavedChanges,
    setUnsavedChanges,
  } = useUnsavedChangesStore();
  const { version, setVersion, resetRefetch, setResetRefetch } =
    useTemplateVersionSelectorStore();
  const toggleActive = useRef(false);
  const isMobile = useIsMobile();
  const refetch = useRef(false);
  const changeVersion = useRef(false);
  const fromSelector = useRef(false);
  // const { unsavedChanges, setUnsavedChanges } =
  //   useTemplateUnsavedChangesStore();

  const categoryOptions = [
    {
      key: "history_present_illness",
      text: "History of Present Illness",
      icon: <FileText size={17} />,
    },
    {
      key: "review_of_systems",
      text: "Review of Systems",
      icon: <Stethoscope size={17} />,
    },
    {
      key: "physical_exam",
      text: "Physical Exam",
      icon: <Heartbeat size={17} />,
    },
    {
      key: "differential_diagnosis",
      text: "Differential Diagnosis",
      icon: <Thermometer size={17} />,
    },
    {
      key: "medical_decision_making",
      text: "Medical Decision Making",
      icon: <Brain size={17} />,
    },
    {
      key: "consults",
      text: "Consults",
      icon: <Users size={17} />,
    },
    {
      key: "procedures",
      text: "Procedures",
      icon: <Syringe size={17} />,
    },
    {
      key: "critical_care_note",
      text: "Critical Care Note",
      icon: <FirstAidKit size={17} />,
    },
    {
      key: "disposition",
      text: "Disposition",
      icon: <Pill size={17} />,
    },
  ];

  const isAdmin = useIsAdmin();
  const { height, width } = useWindowDimensions();

  const {
    handleDeleteTemplate,
    currentCreateTemplate,
    setSaveTemplateFunction,
  }: OutletContextType = useOutletContext();

  const { templateId = "create" } = useParams<{
    templateId: string;
  }>();

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<ITemplate>();

  const saveQueue = useRef<ITemplate | null>(null);
  const isSaving = useRef<boolean>(false);
  const isReplacing = useRef<boolean>(false);

  // Store the current templateId in a ref to ensure we always have the latest value
  const currentTemplateIdRef = useRef(templateId);

  // Update the ref whenever templateId changes
  useEffect(() => {
    currentTemplateIdRef.current = templateId;
  }, [templateId]);

  // Debounced function to update the title
  const debouncedTitleUpdate = useCallback(
    debounce((value: string) => {
      // @ts-ignore
      setValue("title", value);
      setHasUnsavedChanges(true);
      setUnsavedChanges(true);
    }, 300),
    []
  );

  const autoSave = async (value: ITemplate) => {
    if (isSaving.current) {
      return;
    }

    setHasUnsavedChanges(true);
    setUnsavedChanges(true);
  };

  const saveTemplate = async () => {
    if (isSaving.current) {
      return;
    }

    isSaving.current = true;
    let res: any;

    const currentTemplateId = currentTemplateIdRef.current;

    try {
      if (currentTemplateId === "create") {
        const data = {
          title: watch("title") || "New Template",
          content: watch("content") || "",
          is_active: false,
          category: watch("category")!,
        };

        isReplacing.current = true;

        if (isAdmin) {
          res = await createNewAdminTemplate(data);
        } else {
          res = await createNewTemplate(data);
        }
        await navigate(
          `${isAdmin ? "/admin" : ""}/dashboard/templates/${res?.data?.id}`,
          {
            replace: true,
          }
        );

        addTemplate({
          id: res?.data?.id,
          title: watch("title")!,
          category: watch("category")!,
          is_active: res?.data.is_active,
          ...res?.data,
        });

        setTemplate({
          id: res?.data?.id,
          title: watch("title")!,
          category: watch("category")!,
          is_active: res?.data.is_active,
          ...res?.data,
        });

        setCurrentVersion(res?.data?.latest_version);
      } else {
        res = await updateVersion(
          currentVersion?.id!,
          {
            title: watch("title") || "New Template",
            content: watch("content"),
            category: watch("category"),
          },
          isAdmin ? isAdmin : undefined
        );

        addTemplate({
          ...template,
          title: res?.template?.title,
          category: res?.template?.category,
        });
      }

      setHasUnsavedChanges(false);
      setUnsavedChanges(false);
      setSaveSuccess(true);

      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (error: any) {
      console.error("Error saving template:", error);
    } finally {
      isSaving.current = false;
    }
  };

  const saveAsNewVersion = async () => {
    if (isSaving.current || templateId === "create") {
      return;
    }

    setIsSavingNewVersion(true);
    try {
      const data = {
        title: watch("title") || "New Template",
        content: watch("content") || "",
        category: watch("category")!,
      };

      const res = await createVersion(
        templateId,
        data,
        isAdmin ? isAdmin : undefined
      );

      setCurrentVersion(res);
      setNewVersion(res);

      setHasUnsavedChanges(false);
      setUnsavedChanges(false);
      setSaveSuccess(true);

      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error("Error creating new version:", error);
    } finally {
      setIsSavingNewVersion(false);
    }
  };

  const debounceFn = useCallback(debounce(autoSave, 500), []);

  useEffect(() => {
    const subscription = watch((value: any) => {
      if (!canUpdate.current) {
        canUpdate.current = true;
        return;
      }

      debounceFn(value);
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  const fetchTemplate = async (templateId: string, loading = true) => {
    if (loading) setIsLoading(true);

    if (templateId === "create") {
      const temp = {
        id: "create",
        title: "",
        description: "",
        content: "",
        created_at: "",
        updated_at: "",
        category: ctgStore,
      };

      setTemplate(temp);
      reset(temp);
      setCurrentVersion(temp);
      setVersion(temp);
    } else {
      try {
        const response = await getTemplate(
          templateId,
          isAdmin ? isAdmin : undefined
        );

        const version = {
          ...response,
          ...(response?.published_version
            ? {
                published: response?.published_version?.published,
                content: response?.published_version?.content,
              }
            : {
                published: response?.latest_version?.published,
                content: response?.latest_version?.content,
              }),
        };
        reset(version);
        setTemplate(version);

        setCurrentVersion(
          !isEmpty(response?.published_version)
            ? response?.published_version!
            : response?.latest_version!
        );
        setVersion(
          !isEmpty(response?.published_version)
            ? response?.published_version!
            : response?.latest_version!
        );
      } catch (error) {
        console.error(error);
        if (loading) setIsLoading(false);
      }
    }
    canUpdate.current = false;
    setHasUnsavedChanges(false);
    setUnsavedChanges(false);
    if (loading) setIsLoading(false);
  };

  useEffect(() => {
    canUpdate.current = false;
    setHasUnsavedChanges(false);
    setUnsavedChanges(false);
    setPendingNavigation(null);

    if (templateId === "create" && currentCreateTemplate) {
      reset(currentCreateTemplate);
      setTemplate(currentCreateTemplate);

      setIsLoading(false);
      setHasUnsavedChanges(false);
      setUnsavedChanges(false);
      return;
    }

    if (templateId) {
      if (isReplacing.current) {
        isReplacing.current = false;
        return;
      }
      setIsLoading(true);
      setHasUnsavedChanges(false);
      setUnsavedChanges(false);
      addTemplate({} as ITemplate);
      setTemplate({} as ITemplate);
    }
    const res = setTimeout(async () => {
      if (templateId) {
        if (isReplacing.current) {
          isReplacing.current = false;
          return;
        }
        fetchTemplate(templateId);
      }
    }, 500);

    return () => {
      clearTimeout(res);
    };
  }, [templateId, currentCreateTemplate]);

  const resetTemp = async () => {
    try {
      await resetTemplate(templateId);
    } catch (error) {
      //
    } finally {
      setIsModalOpen(false);
    }
  };

  const unpublish = async () => {
    try {
      const res = await markInactive(templateId, isAdmin ? isAdmin : undefined);

      addTemplate({ ...template, is_active: res.is_active });
      setTemplate({ ...template, is_active: res.is_active });
      setCurrentVersion({ ...currentVersion, published: false });

      refetch.current = true;
    } catch (error) {
      //
    } finally {
      setIsModalOpen(false);
    }
  };

  const setTemplateActive = async () => {
    // Check if there are unsaved changes
    if (hasUnsavedChanges) {
      // Show modal indicating changes are being saved
      setModalOpen("activating");
      setIsModalOpen(true);
      return;
    }

    // If no unsaved changes, proceed with activation directly
    await activateTemplate();
  };

  // New function to handle the actual template activation
  const activateTemplate = async () => {
    try {
      // If we were showing a modal, close it
      if (modalOpen === "activating") {
        setIsModalOpen(false);
      }

      const res = await publishTemplate(
        templateId,
        currentVersion?.id!,
        isAdmin ? isAdmin : undefined
      );

      toggleActive.current = true;

      if (res?.published_version) {
        const updatedVersion = {
          ...res.published_version,
          published: true,
        };

        setCurrentVersion(updatedVersion);
      }

      addTemplate({ ...template, is_active: res.is_active, title: res.title });
      setTemplate({ ...template, is_active: res.is_active, title: res.title });

      toast.success("Template published successfully");
    } catch (error: any) {
      console.error("Error publishing template:", error);
    }
  };

  const handleOpenModal = (confirmAction: string) => {
    setIsModalOpen(true);
    setModalOpen(confirmAction);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleToggleNoteMenu = () => {
    setIsNoteMenuOpen(!isNoteMenuOpen);
  };

  const deleteNote = async () => {
    setIsFading(true);
    setTimeout(() => {
      handleDeleteTemplate(templateId);
      setIsFading(false);
    }, 250);
    setIsModalOpen(false);
  };

  const openAllVersions = () => {
    navigate(
      `${isAdmin ? "/admin" : ""}/dashboard/templates/${templateId}?version=${
        template?.latest_version?.version
      }`
    );
    setOpen(true);
  };

  const dropdownItems = [
    ...(isMobile
      ? [
          {
            title: "Set Version as Active",
            href: "#",
            icon: <Check size={20} />,
            onClick: () => setTemplateActive(),
          },
        ]
      : []),
    ...(isMobile && !isAdmin
      ? [
          {
            title: "Share Template",
            href: "#",
            icon: <ShareFat size={20} />,
            onClick: () => setShareModalOpen(true),
          },
        ]
      : []),
    ...(template?.is_active
      ? [
          {
            title: "Mark Template Inactive",
            href: "#",
            icon: <X size={20} />,
            onClick: () => handleOpenModal("unpublish"),
          },
        ]
      : []),
    ...(template?.default && template?.updated
      ? []
      : !template?.default
      ? [
          {
            title: "Delete Template",
            href: "#",
            icon: <TrashSimple size={20} />,
            onClick: () => handleOpenModal("delete"),
          },
        ]
      : []),
  ];

  const insertVariable = (variable: string, regName: string) => {
    const textarea = document.getElementById(regName) as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const value = textarea.value;

    const beforeCursor = value.substring(Math.max(0, start - 1000), start);
    const hasManyBraces = /\{+$/.test(beforeCursor);

    const variableWithBraces = `{{${variable}}}`;
    const newValue =
      value.substring(
        0,
        hasManyBraces
          ? start - (beforeCursor.match(/\{+$/)?.[0]?.length ?? 0)
          : start
      ) +
      variableWithBraces +
      " " +
      value.substring(start);

    // @ts-ignore
    setValue(regName as keyof ITemplate, newValue);

    setTimeout(() => {
      if (textarea) {
        const newPosition =
          start +
          variableWithBraces.length +
          1 -
          (hasManyBraces ? beforeCursor.match(/\{+$/)?.[0]?.length ?? 0 : 0);
        textarea.focus();
        textarea.setSelectionRange(newPosition, newPosition);
      }
    }, 0);

    setInComingVariable(null);
  };

  const handleVersionSelect = (selectedVersion: ITemplateVersion) => {
    if (!selectedVersion) return;

    setCurrentVersion(selectedVersion);

    fetchTemplateVersion(selectedVersion.id);
  };

  // Add beforeunload event listener to prevent closing tab with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        setUnsavedChanges(true);
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (pendingNavigation) {
      if (hasUnsavedChanges) {
        setShowUnsavedDialog(true);
      } else {
        navigate(pendingNavigation);
      }
      return;
    }
  }, [pendingNavigation]);

  // setPendingNavigation(null);
  useEffect(() => {
    if (!showUnsavedDialog) {
      setPendingNavigation(null);
    }
  }, [showUnsavedDialog]);

  const fetchTemplateVersion = async (versionId: string, loading = true) => {
    if (loading) setIsLoading(true);
    try {
      const version = await getTemplateVersion(
        versionId,
        isAdmin ? isAdmin : undefined
      );

      const data = {
        id: templateId!,
        title: version?.template?.title!,
        category: version?.template?.category!,
        content: version?.content,
        published: version?.published,
      };

      setHasUnsavedChanges(false);
      canUpdate.current = false;
      reset(data);
    } catch (error) {
      console.error(error);
    }
    if (loading) setIsLoading(false);
  };

  useEffect(() => {
    if (firstLoad.current) {
      firstLoad.current = false;
      return;
    }
    if (
      !isEmpty(version) &&
      version.id !== currentVersion.id &&
      templateId !== "create"
    ) {
      setCurrentVersion(version);

      fetchTemplateVersion(version.id);
    }
  }, [version]);

  const categoryKey =
    watch("category") || template?.category || "default-category";

  useEffect(() => {
    if (setSaveTemplateFunction) {
      setSaveTemplateFunction(() => saveTemplate);
    }

    return () => {
      // Clean up when component unmounts
      if (setSaveTemplateFunction) {
        setSaveTemplateFunction(null);
      }
    };
  }, [setSaveTemplateFunction]);

  const [isMobileTitleModalOpen, setIsMobileTitleModalOpen] = useState(false);

  const updateTemplateTitle = async (newTitle: string) => {
    if (isSaving.current) {
      return;
    }

    isSaving.current = true;
    try {
      // Set the title in the form
      setValue("title", newTitle);

      // Update the version with the new title
      const res = await updateVersion(
        currentVersion?.id!,
        {
          title: newTitle,
        },
        isAdmin ? isAdmin : undefined
      );

      setHasUnsavedChanges(false);
      setUnsavedChanges(false);
      setSaveSuccess(true);

      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);

      return res;
    } catch (error: any) {
      console.error("Error updating template title:", error);
      throw error;
    } finally {
      isSaving.current = false;
    }
  };

  useEffect(() => {
    if (resetRefetch) {
      fetchTemplate(templateId);
      refetch.current = true;
      setResetRefetch(false);
    }
  }, [resetRefetch]);

  const handleNavigation = (saving?: boolean) => {
    if (saving) {
      saveTemplate();
    }
    setHasUnsavedChanges(false);
    setUnsavedChanges(false);
    setShowUnsavedDialog(false);
    if (fromSelector.current) {
      fromSelector.current = false;
      changeVersion.current = true;
      return;
    }
    if (pendingNavigation) navigate(pendingNavigation);
    setPendingNavigation(null);
  };

  return (
    <>
      <TooltipProvider delayDuration={0}>
        <ResizablePanelGroup direction="horizontal">
          {!!template && (
            <div className="flex w-full">
              {
                <ResizablePanel
                  order={3}
                  id="panel-3"
                  defaultSize={62}
                  collapsedSize={60}
                  collapsible={true}
                  minSize={60}
                  maxSize={90}
                  onResize={(col) => {
                    setTextkey(col.toString());
                  }}
                  className={
                    isMobile
                      ? "bg-white dark:bg-gray-950"
                      : "rounded-lg bg-white dark:bg-gray-950 my-4 mr-4 ml-2 border-[0.5px] dark:border-gray-700 shadow-md"
                  }
                >
                  <div
                    id="top-template-div"
                    className={
                      " bg-white dark:bg-gray-950 " +
                      (width <= 768 ? "w-full" : "w-full")
                    }
                  >
                    <div id="template-editor-div" className="w-full h-full">
                      <div
                        id="template-editor-header"
                        className={
                          "flex h-[56px] p-2 border-b-[0.5px] h-[56px] bg-gray-25/20 dark:bg-gray-800/50 " +
                          (isMobile ? "" : "px-[20px]")
                        }
                      >
                        <div className="w-full flex items-center">
                          <div className="cursor-pointer">
                            <CaretLeft
                              size={15}
                              onClick={() =>
                                navigate(
                                  `${
                                    isAdmin ? "/admin" : ""
                                  }/dashboard/templates`
                                )
                              }
                            />
                          </div>
                          <div className="w-full font-semibold text-xl mr-2 flex items-center">
                            {isTitleEditing ? (
                              <Input
                                placeholder="Add title"
                                required
                                disabled={template.default && !isAdmin}
                                // @ts-ignore
                                defaultValue={watch("title")}
                                className="truncate overflow-hidden border-0 dark:text-white text-xl 
                                  border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-background
                                  focus:bg-white dark:focus:bg-gray-800 
                                  hover:bg-white dark:hover:bg-gray-800
                                  transition-colors duration-200
                                  h-10"
                                onChange={(e) => {
                                  debouncedTitleUpdate(
                                    (e.target as HTMLInputElement).value
                                  );
                                }}
                                onBlur={(e) => {
                                  setIsTitleEditing(false);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    setIsTitleEditing(false);
                                  }
                                }}
                                autoFocus
                              />
                            ) : (
                              <div className="flex items-center w-full">
                                <div
                                  className={
                                    "flex items-center rounded-full transition-colors duration-200 flex-shrink-0 " +
                                    (template.default && !isAdmin
                                      ? "cursor-not-allowed"
                                      : "hover:bg-gray-25 dark:hover:bg-gray-800")
                                  }
                                >
                                  <div
                                    className={
                                      "truncate cursor-pointer h-10 flex items-center pl-2 " +
                                      (isMobile
                                        ? "max-w-[100px]"
                                        : "max-w-[500px]") +
                                      (template.default && !isAdmin
                                        ? " cursor-not-allowed"
                                        : " hover:text-gray-600 dark:hover:text-white")
                                    }
                                    onClick={() => {
                                      if (template.default && !isAdmin) return;

                                      if (isMobile) {
                                        setIsMobileTitleModalOpen(true);
                                      } else {
                                        setIsTitleEditing(true);
                                      }
                                    }}
                                    title={watch("title") || "New Template"}
                                  >
                                    {watch("title") || "New Template"}
                                  </div>
                                  {!template.default && !isAdmin && (
                                    <button
                                      onClick={() => {
                                        if (template.default && !isAdmin)
                                          return;

                                        if (isMobile) {
                                          setIsMobileTitleModalOpen(true);
                                        } else {
                                          setIsTitleEditing(true);
                                        }
                                      }}
                                      className="p-2"
                                    >
                                      <PencilSimple
                                        size={16}
                                        className="text-gray-500 dark:text-white"
                                      />
                                    </button>
                                  )}
                                </div>
                                {/* {template?.is_active && (
                                  <>
                                    {isMobile ? (
                                      <Tooltip>
                                        <TooltipContent
                                          side="top"
                                          className="bg-gray-600 text-white"
                                        >
                                          <p>Template is active</p>
                                        </TooltipContent>
                                        <TooltipTrigger asChild>
                                          <div className="text-green-700 dark:text-green-400">
                                            <CheckCircle size={20} />
                                          </div>
                                        </TooltipTrigger>
                                      </Tooltip>
                                    ) : (
                                      <div className="px-2 flex items-center justify-center border-[1px] h-[24px] border-green-300 dark:border-green-500 rounded-full bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 hover:bg-gray-50 hover:dark:bg-green-950 hover:border-green-300 hover:dark:border-green-500 hover:text-green-700 hover:dark:text-green-400">
                                        <span className="text-sm flex items-center gap-1">
                                          <Check size={16} />
                                          {!isMobile && "Active"}
                                        </span>
                                      </div>
                                    )}
                                  </>
                                )} */}
                              </div>
                            )}
                          </div>

                          <div className="flex items-center justify-center ml-auto">
                            <div className="flex items-center justify-center ml-auto">
                              {!isMobile && (
                                <div className="flex items-center justify-center">
                                  {!currentVersion?.published && (
                                    <Tooltip>
                                      <TooltipContent
                                        side="top"
                                        className="bg-gray-600 text-white"
                                      >
                                        <p>Set as Active</p>
                                      </TooltipContent>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="outline"
                                          size="icon"
                                          className={"h-8 w-8 mr-2"}
                                        >
                                          <CheckFat
                                            onClick={() =>
                                              templateId !== "create"
                                                ? setTemplateActive()
                                                : null
                                            }
                                            size={22}
                                          />
                                        </Button>
                                      </TooltipTrigger>
                                    </Tooltip>
                                  )}
                                  {!isAdmin && (
                                    <Tooltip>
                                      <TooltipContent
                                        side="top"
                                        className="bg-gray-600 text-white"
                                      >
                                        <p>Share Template</p>
                                      </TooltipContent>
                                      <TooltipTrigger asChild>
                                        <Button
                                          variant="outline"
                                          size="icon"
                                          className={"h-8 w-8 mr-2"}
                                        >
                                          <ShareFat
                                            onClick={() =>
                                              setShareModalOpen(true)
                                            }
                                            size={22}
                                          />
                                        </Button>
                                      </TooltipTrigger>
                                    </Tooltip>
                                  )}
                                </div>
                              )}
                              {templateId !== "create" && (
                                <TemplateVersionSelector
                                  templateId={templateId}
                                  currentVersion={currentVersion}
                                  toggleActive={toggleActive.current}
                                  resetActive={() => {
                                    toggleActive.current = false;
                                  }}
                                  newVersion={newVersion}
                                  refetch={refetch.current}
                                  resetRefetch={() => {
                                    refetch.current = false;
                                  }}
                                  onVersionSelect={handleVersionSelect}
                                  selectedVrsn={
                                    !isEmpty(version) ? version : undefined
                                  }
                                  onManageVersions={() => openAllVersions()}
                                  setShowUnsavedDialog={(show) => {
                                    fromSelector.current = true;
                                    setShowUnsavedDialog(show);
                                  }}
                                  changeVersion={changeVersion.current}
                                  resetChangeVersion={() => {
                                    changeVersion.current = false;
                                  }}
                                />
                              )}
                              {
                                <div
                                  className={
                                    "flex items-center justify-center mr-2 h-[32px] " +
                                    (template.default &&
                                    currentVersion.version == "1" &&
                                    !isAdmin
                                      ? isMobile
                                        ? ""
                                        : "w-[180px]"
                                      : isMobile
                                      ? "w-[60px]"
                                      : "w-[100px]")
                                  }
                                >
                                  {saveSuccess ? (
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      className={
                                        "h-[32px] " +
                                        (template.default &&
                                        currentVersion.version == "1" &&
                                        !isAdmin
                                          ? isMobile
                                            ? ""
                                            : "w-[180px]"
                                          : isMobile
                                          ? "w-[60px]"
                                          : "w-[100px]")
                                      }
                                    >
                                      <div className="text-green-600 dark:text-green-400 flex items-center">
                                        <Check
                                          size={17}
                                          className={
                                            "" + (isMobile ? "" : "mr-1")
                                          }
                                        />
                                        {!isMobile && "Saved"}
                                      </div>
                                    </Button>
                                  ) : (
                                    <DropdownMenu>
                                      <div
                                        className={
                                          "transition-all duration-300 ease-in-out " +
                                            template.default &&
                                          currentVersion.version == "1" &&
                                          !isAdmin
                                            ? "flex items-center w-[180px] h-[32px] "
                                            : "flex items-center  " +
                                              (isMobile
                                                ? "w-[60px]"
                                                : "w-[100px]")
                                        }
                                      >
                                        <Button
                                          onClick={() => {
                                            if (
                                              template.default &&
                                              currentVersion.version == "1" &&
                                              !isAdmin
                                            ) {
                                              saveAsNewVersion();
                                            } else {
                                              saveTemplate();
                                            }
                                          }}
                                          variant={
                                            saveSuccess ? "outline" : "default"
                                          }
                                          className={
                                            "px-2 text-base flex items-center justify-center h-[32px] text-white " +
                                            (templateId === "create" ||
                                            (template.default &&
                                              currentVersion.version == "1" &&
                                              !isAdmin) ||
                                            saveSuccess
                                              ? "rounded-md"
                                              : "rounded-l-md rounded-r-none") +
                                            (saveSuccess
                                              ? " border-green-600 "
                                              : " bg-sky-200 hover:bg-sky-100") +
                                            (isMobile ? "" : " w-full")
                                          }
                                        >
                                          <div className="flex items-center">
                                            <FloppyDisk
                                              size={17}
                                              weight="fill"
                                              className={
                                                "" + (isMobile ? "" : "mr-1")
                                              }
                                            />
                                            {!isMobile && (
                                              <div>
                                                {template.default &&
                                                currentVersion.version == "1" &&
                                                !isAdmin
                                                  ? "Save as New Version"
                                                  : "Save"}
                                              </div>
                                            )}
                                          </div>
                                        </Button>
                                        {templateId !== "create" &&
                                          !(
                                            template.default &&
                                            currentVersion.version == "1" &&
                                            !isAdmin
                                          ) && (
                                            <DropdownMenuTrigger asChild>
                                              <Button className="w-[40px] flex items-center h-[32px] pl-1 pr-2 rounded-l-none rounded-r-md bg-sky-200 hover:bg-sky-100 text-white border-l border-sky-100">
                                                {!saveSuccess && (
                                                  <CaretDown
                                                    size={15}
                                                    weight="bold"
                                                  />
                                                )}
                                              </Button>
                                            </DropdownMenuTrigger>
                                          )}
                                      </div>
                                      <DropdownMenuContent
                                        align="end"
                                        className="dark:bg-gray-800 dark:border-gray-700"
                                      >
                                        <DropdownMenuItem
                                          onClick={saveAsNewVersion}
                                          className="cursor-pointer flex items-center gap-2 dark:text-white dark:hover:bg-gray-600"
                                          disabled={
                                            templateId === "create" ||
                                            isSavingNewVersion
                                          }
                                        >
                                          <FloppyDisk size={18} />
                                          <span>Save as New Version</span>
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  )}
                                </div>
                              }
                              {templateId !== "create" &&
                                !!dropdownItems.length && (
                                  <div
                                    className="flex items-center justify-center rounded-full ml-auto"
                                    onClick={handleToggleNoteMenu}
                                  >
                                    <NoteDropdown
                                      items={dropdownItems}
                                      contentW="w-[220px]"
                                    />
                                  </div>
                                )}
                            </div>
                          </div>
                        </div>
                        <Toaster />
                      </div>
                      <div
                        className={canAnim ? "template-section" : "pt-5"}
                        style={{
                          overflow: "auto",
                          height: `calc(100dvh - ${isMobile ? 120 : 160}px)`,
                        }}
                      >
                        {isLoading ? (
                          <>
                            {[1, 2, 3, 4].map((index) => (
                              <div
                                key={index}
                                className="px-4 py-2 sm:gap-4 sm:px-6"
                              >
                                <NoteSectionPending />
                              </div>
                            ))}
                          </>
                        ) : (
                          <>
                            <TemplateSection
                              key="category"
                              title="Category"
                              sectionKey="category"
                              collapsedKey={false}
                              insertVariable={(variable) =>
                                insertVariable(variable!, "category")
                              }
                              showPopup={false}
                              variables={[]}
                            >
                              <Select
                                key={categoryKey}
                                onValueChange={(val) => {
                                  setValue("category", val);
                                }}
                                disabled={template.default && !isAdmin}
                                value={watch("category")}
                              >
                                <SelectTrigger className="min-w-[180px]">
                                  <SelectValue placeholder="Select category" />
                                </SelectTrigger>
                                <SelectContent className="bg-white dark:bg-gray-800">
                                  <SelectGroup>
                                    {categoryOptions.map((sort) => {
                                      return (
                                        <SelectItem
                                          key={sort.key}
                                          className="hover:bg-gray-25 dark:hover:bg-gray-500"
                                          value={sort.key!}
                                        >
                                          <div
                                            className={
                                              "flex items-center " +
                                              (sort.key === null
                                                ? "text-gray-500"
                                                : "")
                                            }
                                          >
                                            <div className="mr-2">
                                              {sort.key === null ? (
                                                <FunnelSimple size={17} />
                                              ) : (
                                                sort.icon
                                              )}
                                            </div>
                                            {sort.text}
                                          </div>
                                        </SelectItem>
                                      );
                                    })}
                                  </SelectGroup>
                                </SelectContent>
                              </Select>
                            </TemplateSection>
                            <TemplateSection
                              key="content"
                              title={NOTE["content"]}
                              sectionKey="content"
                              collapsedKey={false}
                              insertVariable={(variable) =>
                                insertVariable(variable!, "content")
                              }
                              variables={MEDICAL_VARIABLES["content"]}
                            >
                              <EnhancedTextarea
                                variables={MEDICAL_VARIABLES["content"]}
                                id="content"
                                placeholder="Include relevant information based on the selected category."
                                className="overflow-hidden border-0 dark:text-white text-base border-gray-300 dark:border-gray-600 bg-white dark:bg-background"
                                setValue={setValue}
                                register={register(`content`)}
                              />
                            </TemplateSection>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </ResizablePanel>
              }
            </div>
          )}
        </ResizablePanelGroup>
      </TooltipProvider>
      <ConfirmationModal
        isOpen={isModalOpen}
        heading={
          modalOpen === "delete"
            ? "Delete template"
            : modalOpen === "unpublish"
            ? "Unpublish template"
            : modalOpen === "unarchive"
            ? "Unarchive template"
            : modalOpen === "sign"
            ? "Save template"
            : modalOpen === "activating"
            ? "Activating Template"
            : "Reset template"
        }
        message={
          modalOpen === "delete"
            ? "Are you sure you want to delete your template? All of your data will be permanently removed. This action cannot be undone."
            : modalOpen === "unpublish"
            ? "Are you sure you want to mark your template as inactive?"
            : modalOpen === "unarchive"
            ? "Are you sure you want to unarchive your template?"
            : modalOpen === "sign"
            ? "Are you sure you want to save your template? This will create a new version."
            : modalOpen === "activating"
            ? "You have unsaved changes. Do you want to save these changes before activating the template?"
            : "Are you sure you want to reset your template?"
        }
        confirmAction={
          modalOpen === "delete"
            ? "Delete"
            : modalOpen === "unpublish"
            ? "Mark as Inactive"
            : modalOpen === "unarchive"
            ? "Unarchive"
            : modalOpen === "sign"
            ? "Save"
            : modalOpen === "activating"
            ? "Save and Activate"
            : "Reset"
        }
        onClose={handleCloseModal}
        onConfirm={
          modalOpen === "delete"
            ? deleteNote
            : modalOpen === "reset"
            ? resetTemp
            : modalOpen === "sign"
            ? saveTemplate
            : modalOpen === "activating"
            ? async () => {
                // First save the template
                setIsModalOpen(false);
                await saveTemplate();
                // Then activate it
                await activateTemplate();
              }
            : unpublish
        }
      />
      <TemplateVersionsModal
        handleSelect={(option) => {}}
        open={open}
        setOpen={setOpen}
      />
      <ShareTemplateModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        onShare={() => {}}
        template={template}
      />
      <EditTitleModal
        isOpen={isMobileTitleModalOpen}
        // @ts-ignore
        currentTitle={watch("title") || "New Template"}
        onClose={() => setIsMobileTitleModalOpen(false)}
        onSave={async (newTitle: string) => {
          await updateTemplateTitle(newTitle);
          setIsMobileTitleModalOpen(false);
        }}
        isLoading={isSaving.current}
        heading="Template"
      />
      <UnsavedChangesDialog
        showUnsavedDialog={showUnsavedDialog}
        setShowUnsavedDialog={setShowUnsavedDialog}
        confirmNavigation={() => {
          handleNavigation(true);
        }}
        cancelNavigation={() => {
          setShowUnsavedDialog(false);
          setPendingNavigation(null);
        }}
        discardChanges={() => {
          handleNavigation(false);
        }}
      />
    </>
  );
};

export default Template;
